/**
 * CivilRespondFooter.tsx — v0.4.9.2.3c13 PWA
 *
 * What's fixed in c13:
 *   1. VERSION: uses `__APP_VERSION__` injected by vite.config.ts
 *      (same pattern as AdminFooter.tsx and GuardFooter.tsx).
 *      The build config must have `define: { __APP_VERSION__: ... }`
 *      — if it's missing, the bare reference would throw a
 *      ReferenceError at component mount, crashing the React tree.
 *      See the deployment note for the vite.config.ts patch.
 *   2. ROLE GUARD on Admin mode line: only show "Admin mode — <org>"
 *      if the user is actually sadmin / superadmin. Previously the
 *      guard was on the Switch Org button only, so the "Admin mode"
 *      text showed up for any user (e.g. an agent) if localStorage
 *      had a stale civilRespond_actAsOrgId from a previous SuperAdmin
 *      session on the same device.
 *   3. NON-ADMIN SELF-CLEAN: if we somehow still have a stale
 *      civilRespond_actAsOrgId for a non-admin user (e.g. they had
 *      it set before this fix rolled out), we clear it on render.
 *   4. CORRECT STATION: shows the agent's OWN station_id and country
 *      from the user object — not the actAsOrgId. The actAsOrgId is
 *      only used by the admin-mode line (which is now role-gated).
 *
 * The footer is HIDDEN when offline (matches v0.4.1 behavior).
 */
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConnectivity } from '../../hooks/useConnectivity';

// =====================================================================
// Version injection
//
// `declare const` is a TypeScript compile-time annotation that emits
// ZERO runtime code. The actual value of __APP_VERSION__ must be
// supplied by the bundler. With Vite, this means vite.config.ts must
// have:
//
//   import pkg from './package.json';
//   export default defineConfig({
//     define: {
//       __APP_VERSION__: JSON.stringify(pkg.version),
//     },
//     plugins: [...]
//   });
//
// If the define block is missing, the bare reference `__APP_VERSION__`
// below will throw a ReferenceError at component mount time and the
// React tree will crash (white screen on APK). That was the trap the
// PWA hit during c3-c9 deployments. Confirm your vite.config.ts has
// the define block before deploying this.
// =====================================================================
declare const __APP_VERSION__: string;

export default function CivilRespondFooter() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isOnline } = useConnectivity();

  // v0.4.9.2.3c13: SELF-CLEAN stale admin-org state for non-admin
  // users (one-time cleanup for users who logged in with c11 or
  // earlier and got stuck with a foreign actAsOrgId).
  useEffect(() => {
    if (!user) return;
    const role = String(user.role || '').toLowerCase();
    const isAdmin = role === 'sadmin' || role === 'superadmin';
    if (!isAdmin && typeof window !== 'undefined' && window.localStorage) {
      try {
        if (localStorage.getItem('civilRespond_actAsOrgId')) {
          localStorage.removeItem('civilRespond_actAsOrgId');
          localStorage.removeItem('civilRespond_actAsOrgs');
          // We don't reload — the next render will show the correct
          // footer. (Reloading would interrupt the agent.)
        }
      } catch { /* ignore */ }
    }
  }, [user?.role]);

  // Hidden when offline — same as v0.4.1 base.
  if (!isOnline) return null;

  // Don't render if no user yet.
  if (!user) return null;

  const role = String(user.role || 'user').toLowerCase();
  const isAdmin = role === 'sadmin' || role === 'superadmin';
  const roleLabel = role || 'user';
  const userLabel = user.name || user.email || 'Unknown';

  // Determine the agent's own station/country for the user-identification
  // line. Priority:
  //   1. user.station_id + user.country (from AuthContext login response)
  //   2. user.stores[0].station_name + user.stores[0].country (older shape)
  //   3. fall back to user.country_code
  //   4. fallback to '—'
  const stationId: string | null =
    (user as any).station_id ||
    (Array.isArray((user as any).stores) && (user as any).stores[0]?.station_id) ||
    (user as any).country_code ||
    null;

  // Admin-mode line — only shown for sadmin/superadmin.
  // For agents: NEVER show, even if localStorage has actAsOrgId.
  let adminModeLabel: string | null = null;
  if (isAdmin) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('civilRespond_actAsOrgId');
        if (stored) {
          const mapRaw = window.localStorage.getItem('civilRespond_actAsOrgs');
          if (mapRaw) {
            try {
              const map = JSON.parse(mapRaw);
              if (map && typeof map === 'object' && typeof map[stored] === 'string') {
                adminModeLabel = map[stored];
              }
            } catch { /* corrupt map */ }
          }
          if (!adminModeLabel) adminModeLabel = stored;
        }
      }
    } catch { /* ignore */ }
  }

  const handleSwitchOrg = () => {
    try {
      localStorage.removeItem('civilRespond_actAsOrgId');
    } catch { /* ignore */ }
    if (typeof window !== 'undefined') {
      window.location.assign('/civil-respondent');
    }
  };

  return (
    <div className="flex-shrink-0">
      <footer className="text-center text-xs text-muted-foreground py-1.5 border-t leading-tight">
        {/* Admin-mode line — ONLY for sadmin/superadmin */}
        {isAdmin && adminModeLabel && (
          <div className="mb-0.5">
            <span className="font-semibold text-foreground">
              {t('civil.common.adminMode') || 'Admin mode'}
            </span>
            <span className="mx-1">—</span>
            <span className="font-mono">{adminModeLabel}</span>
            <span className="mx-1.5 opacity-50">|</span>
            <button
              type="button"
              onClick={handleSwitchOrg}
              className="text-blue-600 hover:underline"
              data-testid="civil-respondent-footer-switch-org"
            >
              Switch org
            </button>
          </div>
        )}

        {/* Always-present user identification line */}
        <div className="opacity-70">
          <span className="uppercase tracking-wider mr-1.5">{roleLabel}</span>
          <span>
            {t('guard.user')}: {userLabel}
            {stationId && (
              <>
                <span className="mx-1.5 opacity-50">·</span>
                <span className="font-mono opacity-80">{stationId}</span>
              </>
            )}
          </span>
          <span className="mx-1.5 opacity-50">|</span>
          <span className="opacity-60">v.{__APP_VERSION__}</span>
        </div>
      </footer>
    </div>
  );
}
